import { DateTime } from '@ksyos/date-time';
import {
    Badge,
    Card,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { orderBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import type { Profession } from '@common/enums/';
import type { IServiceGroupMembership } from '@common/interfaces/';
import { ManagerAuthorization } from '@common/enums/';
import { getDisplayName } from '@common/utils/getDisplayName';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, SortDirection, toLowerCase, toUpperCase, useAbortableFetch } from '@frontend/utils/';
import type { IPractitioner } from '../../interfaces/practitioner';
import type { IService } from '../../interfaces/service';
import type { IServiceConsumer } from '../../interfaces/serviceConsumer';
import type { IServiceProvider } from '../../interfaces/serviceProvider';
import type { IServiceReader } from '../../interfaces/serviceReader';
import type { IServiceSupervisor } from '../../interfaces/serviceSupervisor';

interface IEmploymentWithServices {
    endDate: string | null;
    id: string;
    isActive: boolean;
    organization: {
        id: string;
        name?: string;
    };
    practitioner: Partial<IPractitioner>;
    profession: Profession | null;
    serviceConsumers: IServiceConsumer[];
    serviceProviders: IServiceProvider[];
    serviceReaders: IServiceReader[];
    serviceSupervisors: IServiceSupervisor[];
    startDate: string;
}

interface ICarePath {
    episodeCategoryId: string;
    episodeCategoryName: string;
    serviceGroups: {
        id: string;
        name: string;
    }[];
}

export interface IOrganizationActiveCareProvidersViewProps {
    organizationId: string;
}

export function OrganizationActiveCareProvidersView(props: IOrganizationActiveCareProvidersViewProps) {
    const abortableFetch = useAbortableFetch();

    const [services, setServices] = useState<IService[]>([]);
    const [serviceGroupMemberships, setServiceGroupMemberships] = useState<IServiceGroupMembership[]>([]);
    const [employmentWithServices, setEmploymentWithServices] = useState<IEmploymentWithServices[]>([]);
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);
    assertHasAuthorization(ManagerAuthorization.organizationRead);

    useEffect(() => {
        loadServices();
        loadServiceGroupMemberships();
    }, []);

    useEffect(() => {
        load();
    }, [props.organizationId]);

    return (
        <Card>
            {getPractitionerTablePartitions(employmentWithServices).map((partition, i) => (
                <React.Fragment key={`${partition.toString()}-${i}`}>
                    {!!partition.employments.length && partition.header}
                    <TableContainer>
                        <Table>
                            {i === 0 && (
                                <TableHead>
                                    <TableRow>
                                        <TableHeader
                                            isSortable
                                            isSortHeader
                                            onClick={() => {
                                                const newSortDirection =
                                                    sortDirection === SortDirection.asc
                                                        ? SortDirection.desc
                                                        : SortDirection.asc;
                                                setSortDirection(newSortDirection);
                                                setEmploymentWithServices(
                                                    sort(employmentWithServices, newSortDirection)
                                                );
                                            }}
                                            sortDirection={toUpperCase(sortDirection)}
                                        >
                                            {translateKey('common.name')}
                                        </TableHeader>
                                        <TableHeader>{translateKey('employment.profession')}</TableHeader>
                                        <TableHeader>{translateKey('episodeCategory.episodeCategories')}</TableHeader>
                                    </TableRow>
                                </TableHead>
                            )}
                            <TableBody>
                                {partition.employments.map((practitionerEmploymentService) => {
                                    const carePathsDescription = getCarePathsDescription(practitionerEmploymentService);

                                    return (
                                        <TableRow key={practitionerEmploymentService.id}>
                                            <TableCell
                                                color={practitionerEmploymentService.isActive ? 'subtle' : 'minimal'}
                                            >
                                                {renderDisplayName(practitionerEmploymentService)}
                                            </TableCell>
                                            <TableCell
                                                color={practitionerEmploymentService.isActive ? 'subtle' : 'minimal'}
                                            >
                                                {practitionerEmploymentService.profession
                                                    ? translateKey(
                                                          `professionEnum.${practitionerEmploymentService.profession}`
                                                      )
                                                    : '-'}
                                            </TableCell>
                                            <TableCell
                                                color={practitionerEmploymentService.isActive ? 'subtle' : 'minimal'}
                                            >
                                                {carePathsDescription.length < 30 && carePathsDescription}
                                                {carePathsDescription.length >= 30 && (
                                                    <Popover content={carePathsDescription}>
                                                        {`${carePathsDescription.substring(0, 30)}...`}
                                                    </Popover>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </React.Fragment>
            ))}
        </Card>
    );

    function renderDisplayName(practitionerEmploymentService: IEmploymentWithServices) {
        return practitionerEmploymentService.isActive ? (
            getDisplayName(practitionerEmploymentService.practitioner, false, 'familyNameCommaInitials')
        ) : (
            <>
                {getDisplayName(practitionerEmploymentService.practitioner, false, 'familyNameCommaInitials')}
                <Badge
                    size="small"
                    sx={{ ml: 2 }}
                    text={translateKey('common.isInactive')}
                />
            </>
        );
    }

    function getCarePathsDescription(employment: IEmploymentWithServices) {
        const carePaths: ICarePath[] = [];

        const serviceProviders = getServiceRights(employment.serviceProviders, employment.isActive);
        const serviceConsumers = getServiceRights(employment.serviceConsumers, employment.isActive);
        const serviceSupervisors = getServiceRights(employment.serviceSupervisors, employment.isActive);
        const serviceReaders = getServiceRights(employment.serviceReaders, employment.isActive);

        addCarePaths(carePaths, serviceProviders);
        addCarePaths(carePaths, serviceConsumers);
        addCarePaths(carePaths, serviceSupervisors);
        addCarePathsFromServiceReaders(carePaths, serviceReaders);
        orderBy(carePaths, (carePath) => carePath.episodeCategoryName);

        let description = '';
        for (const carePath of carePaths) {
            description += ` ${carePath.episodeCategoryName}`;
            if (carePath.serviceGroups.length > 0) {
                const serviceGroups = orderBy(carePath.serviceGroups, (serviceGroup) => serviceGroup.name);
                description += ` (${serviceGroups.map((serviceGroup) => serviceGroup.name).join(', ')})`;
            }
            description += ',';
        }

        return description.slice(1, description.length - 1);
    }

    function getServiceRights<T extends IServiceConsumer | IServiceProvider | IServiceSupervisor | IServiceReader>(
        serviceRights: T[],
        isActiveEmployment: boolean
    ) {
        return isActiveEmployment
            ? serviceRights.filter((serviceRight) => isActiveOnDate(serviceRight, new Date()))
            : serviceRights;
    }

    function addCarePaths<T extends { service: { id: string } }[]>(carePaths: ICarePath[], serviceItems: T) {
        for (const serviceItem of serviceItems) {
            const service = services.find((item) => item.id === serviceItem.service.id);
            if (service) {
                let carePath = carePaths.find((cp) => cp.episodeCategoryId === service.episodeCategory.id);
                if (!carePath) {
                    carePath = {
                        episodeCategoryId: service.episodeCategory.id,
                        episodeCategoryName: translateKey(`episodeCategoryEnum.${service.episodeCategory.name}`),
                        serviceGroups: [],
                    };
                    carePaths.push(carePath);
                }

                const filteredServiceGroupMemberships = serviceGroupMemberships.filter(
                    (membership) => membership.service.id === service.id
                );
                for (const serviceGroupMembership of filteredServiceGroupMemberships) {
                    const serviceGroup = carePath.serviceGroups.find(
                        (sg) => sg.id === serviceGroupMembership.serviceGroup.id
                    );
                    if (!serviceGroup) {
                        carePath.serviceGroups.push({
                            id: serviceGroupMembership.serviceGroup.id,
                            name: serviceGroupMembership.serviceGroup.name,
                        });
                    }
                }
            }
        }
    }

    function addCarePathsFromServiceReaders(carePaths: ICarePath[], serviceReaders: IServiceReader[]) {
        for (const serviceReader of serviceReaders) {
            let carePath = carePaths.find((cp) => cp.episodeCategoryId === serviceReader.episodeCategory.id);
            if (!carePath) {
                carePath = {
                    episodeCategoryId: serviceReader.episodeCategory.id,
                    episodeCategoryName: translateKey(`episodeCategoryEnum.${serviceReader.episodeCategory.name}`),
                    serviceGroups: [],
                };
                carePaths.push(carePath);
            }
        }
    }

    async function load() {
        const url = `/api/employments/${props.organizationId}/services`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const employments = result.json.items as IEmploymentWithServices[];

        setEmploymentWithServices(sort(employments, SortDirection.asc));
    }

    async function loadServices() {
        const result = (await abortableFetch.fetchJson('/api/services?selectionList=true', 'GET')).json.items;
        setServices(result);
    }

    async function loadServiceGroupMemberships() {
        const result = (await abortableFetch.fetchJson('/api/serviceGroupMemberships', 'GET')).json.items;
        setServiceGroupMemberships(result);
    }

    function sort(employments: IEmploymentWithServices[], direction: SortDirection) {
        return orderBy(
            employments,
            [
                'isActive',
                (employmentService) => getDisplayName(employmentService.practitioner, false, 'familyNameCommaInitials'),
            ],
            [SortDirection.desc, toLowerCase(direction)]
        );
    }

    function getPractitionerTablePartitions(employmentList: IEmploymentWithServices[]) {
        const presentEmployments: IEmploymentWithServices[] = [];
        const pastEmployments: IEmploymentWithServices[] = [];
        const futureEmployments: IEmploymentWithServices[] = [];

        employmentList.forEach((employment) => {
            if (DateTime.from(employment.startDate).isBefore(new Date())) {
                if (!employment.endDate || DateTime.from(employment.endDate).isAfter(new Date())) {
                    presentEmployments.push(employment);
                } else {
                    pastEmployments.push(employment);
                }
            } else {
                futureEmployments.push(employment);
            }
        });

        const result = [
            {
                header: <Card.Header title={translateKey('organization.practitioners')} />,
                employments: presentEmployments,
            },
            {
                header: (
                    <Text
                        color="subtle"
                        fontWeight="medium"
                        size="md"
                        sx={{ pt: 6, pb: 4, px: [4, 6, null, 8] }}
                    >
                        {translateKey('organization.practitionersWithFutureEmployments')}
                    </Text>
                ),
                employments: futureEmployments,
            },
            {
                header: (
                    <Text
                        color="subtle"
                        fontWeight="medium"
                        size="md"
                        sx={{ pt: 6, pb: 4, px: [4, 6, null, 8] }}
                    >
                        {translateKey('organization.practitionersWithPastEmployments')}
                    </Text>
                ),
                employments: pastEmployments,
            },
        ];

        return result;
    }
}
